import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import * as Styled from '../LoyaltyPageSeparated.styles';
import { selectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';
import {
  CurrentReceiptData,
  GetClientOrdersData,
} from '../../LoyaltyPage/types';
import ReceiptTableSeparated from './ReceiptTableSeparated';
import CustomCheckbox from '../../../components/CustomCheckbox';
import bottomArrow from '../../../global/media/bottom-arrow.svg';
import {
  deleteFromLocalStorage,
  setToLocalStorage,
} from '../../../global/helpers';

interface LoyaltyReceiptScreenProps {
  training?: boolean;
  showWithBonuses: boolean;
  setShowWithBonuses: (val: boolean) => void;
  allowBonusPreview?: boolean;
  variant?: 'legacy' | 'v3';
  onProceedToConfirm: () => void;
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  pendingOrder?: GetClientOrdersData | null;
}

const LoyaltyReceiptScreen: React.FC<LoyaltyReceiptScreenProps> = ({
  training = false,
  showWithBonuses,
  setShowWithBonuses,
  allowBonusPreview = true,
  variant = 'legacy',
  onProceedToConfirm,
  partnerPercent,
  isStadaBonusesCredit,
  pendingOrder = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentReceipt = useSelector(selectors.currentReceipt);
  const currentReceiptLoading = useSelector(selectors.currentReceiptLoading);
  const currentReceiptError = useSelector(selectors.currentReceiptError);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const submitOrderResult = useSelector(selectors.submitOrderResult);
  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);
  const hasLoyaltyClient = !!clientInfo?.phone;

  const clientBalance = (clientInfo && clientInfo.balance) ?? 0;
  const stadaBalance = (stadaInfo && stadaInfo.balance) ?? 0;

  const totalPrice = useMemo(() => {
    if (!Array.isArray(chosenReceipt?.items)) return 0;
    return (chosenReceipt?.items || []).reduce(
      (acc: number, item: any) => acc + Number(item?.total_price ?? 0),
      0
    );
  }, [chosenReceipt]);

  // Есть ли в чеке штада-товары
  const hasStadaItems = useMemo(
    () => chosenReceipt?.items?.some((item) => item.stadaPercent > 0) ?? false,
    [chosenReceipt]
  );

  const currentItems = useMemo(
    () =>
      distributeClientBonus(
        chosenReceipt?.items ?? [],
        showWithBonuses ? clientBalance : 0,
        partnerPercent,
        showWithBonuses && hasStadaItems && isStadaBonusesCredit
          ? stadaBalance
          : 0
      ),
    [
      chosenReceipt,
      clientBalance,
      stadaBalance,
      showWithBonuses,
      hasStadaItems,
      isStadaBonusesCredit,
      partnerPercent,
    ]
  );

  const totalBonuses = (
    bonuses: 'bonusesToClient' | 'bonusesToPharmacist' | 'stadaBonus'
  ) =>
    Math.floor(
      currentItems?.reduce((acc, item) => acc + item[bonuses], 0) || 0
    );

  const finalPrice = useMemo(() => {
    const discount = currentItems.reduce(
      (acc, item) => acc + item.appliedBonus,
      0
    );
    const result = Number(totalPrice) - Number(discount);
    return result > 0 ? result : 0;
  }, [currentItems, totalPrice]);

  // Очищаем localStorage при снятии выбора
  useEffect(() => {
    if (!chosenReceipt) {
      if (!training) deleteFromLocalStorage('chosenReceipt');
    }
  }, [chosenReceipt, training]);

  // Сбрасываем при ошибке чека
  useEffect(() => {
    if (currentReceiptError) {
      if (!training) deleteFromLocalStorage('chosenReceipt');
      dispatch(actions.setChosenReceipt.request(null));
    }
  }, [currentReceiptError, dispatch, training]);

  const handleSelectReceipt = (item: CurrentReceiptData) => {
    if (!training) setToLocalStorage('chosenReceipt', item);
    dispatch(actions.setChosenReceipt.request(item));
  };

  const handleBackToReceipts = () => {
    if (!training) deleteFromLocalStorage('chosenReceipt');
    dispatch(actions.setChosenReceipt.request(null));
  };

  const pendingOrderNotice = pendingOrder ? (
    <Styled.PendingOrderNotice role="alert">
      <strong>{t('LoyaltyV3.PENDING_ORDER_TITLE')}</strong>
      <span>
        {t('LoyaltyV3.PENDING_ORDER_WITHOUT_RECEIPT', {
          orderId: pendingOrder.order_id,
          docId: pendingOrder.doc_id,
        })}
      </span>
    </Styled.PendingOrderNotice>
  ) : null;

  // Экран с выбранным чеком
  if (chosenReceipt) {
    return (
      <Styled.ReceiptBlock>
        {pendingOrderNotice}
        <Styled.ReceiptChosenHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!submitOrderResult &&
              (variant === 'v3' ? (
                <Styled.V3BackButton
                  $inHeader
                  type="button"
                  onClick={handleBackToReceipts}
                >
                  ← {t('LoyaltyPage.BACK_TO_RECEIPTS')}
                </Styled.V3BackButton>
              ) : (
                <Styled.BackButton onClick={handleBackToReceipts}>
                  <img src={bottomArrow} alt="back" />
                  {t('LoyaltyPage.BACK_TO_RECEIPTS')}
                </Styled.BackButton>
              ))}
            <Styled.BlockTitle style={{ margin: 0 }}>
              {t('LoyaltyPage.ACTIVE_RECEIPT')} №{chosenReceipt.doc_id}
            </Styled.BlockTitle>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {allowBonusPreview && (
              <CustomCheckbox
                title={t('LoyaltyPage.SHOW_BONUSES_SUMM')}
                id="showBonuses"
                onChange={() => setShowWithBonuses(!showWithBonuses)}
                checked={showWithBonuses}
              />
            )}
          </div>
        </Styled.ReceiptChosenHeader>

        <ReceiptTableSeparated
          currentReceipt={chosenReceipt.items}
          showWithBonuses={showWithBonuses}
          partnerPercent={partnerPercent}
          isStadaBonusesCredit={isStadaBonusesCredit}
          showCurrency={variant !== 'v3'}
          showBonusAccrual={hasLoyaltyClient}
          compact={variant === 'v3'}
        />

        {totalPrice > 0 && (
          <Styled.PriceSummary>
            {hasLoyaltyClient && (
              <Styled.PriceRow>
                <span>{t('LoyaltyPage.BONUSES_WILL_BE_ADDED')}:</span>
                <span>{totalBonuses('bonusesToClient')} Б</span>
              </Styled.PriceRow>
            )}
            {hasLoyaltyClient &&
              chosenReceipt.items.some((item) => item.stadaPercent > 0) &&
              isStadaBonusesCredit && (
                <Styled.PriceRow>
                  <span>{t('LoyaltyPage.BONUSES_STADA_WILL_BE_ADDED')}:</span>
                  <span>{totalBonuses('stadaBonus')} Б</span>
                </Styled.PriceRow>
              )}
            <Styled.PriceRow bold>
              <span>{t('LoyaltyPage.TOTAL')}</span>
              <span>{totalPrice} тг</span>
            </Styled.PriceRow>
            {showWithBonuses && (
              <Styled.PriceRow green>
                <span>{t('LoyaltyPage.TOTAL_AFTER_DISCOUNT')}:</span>
                <span>{finalPrice} тг</span>
              </Styled.PriceRow>
            )}
          </Styled.PriceSummary>
        )}

        {/* Кнопка перехода к оформлению */}
        {!submitOrderResult && (
          <Styled.ProceedButton onClick={onProceedToConfirm}>
            {t(
              variant === 'v3'
                ? 'LoyaltyV3.PROCEED_TO_BONUSES'
                : 'LoyaltySeparated.PROCEED_TO_CONFIRM'
            )}{' '}
            →
          </Styled.ProceedButton>
        )}
      </Styled.ReceiptBlock>
    );
  }

  // Экран со всеми доступными чеками
  return (
    <Styled.ReceiptBlock>
      {pendingOrderNotice}
      <Styled.BlockTitle>
        {t(
          variant === 'v3'
            ? 'LoyaltyV3.SELECT_ACTIVE_RECEIPT'
            : 'LoyaltySeparated.RECEIPTS'
        )}
      </Styled.BlockTitle>
      <Styled.BlockHint>{t('LoyaltySeparated.RECEIPTS_HINT')}</Styled.BlockHint>

      {currentReceiptError && (
        <Styled.ErrorLabel>
          {t(`LoyaltyPage.${currentReceiptError?.message}`)}
        </Styled.ErrorLabel>
      )}

      {currentReceiptLoading && !currentReceipt && (
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}
        >
          <CircularProgress size={24} />
        </div>
      )}

      {!currentReceiptError &&
        (currentReceipt && currentReceipt.length > 0 ? (
          currentReceipt.map((item: CurrentReceiptData) => (
            <Styled.ReceiptListItem
              key={item.doc_id}
              onClick={() => handleSelectReceipt(item)}
            >
              <h3 className="receipt-title">
                {t('LoyaltyPage.ACTIVE_RECEIPT')} №{item.doc_id}
              </h3>
              {item.items?.map((product, index) => (
                <div className="receipt-product" key={product.sku}>
                  {index + 1}. {product.name} × {product.quantity}{' '}
                  {t('LoyaltySeparated.PCS')}
                </div>
              ))}
            </Styled.ReceiptListItem>
          ))
        ) : (
          <Styled.ReceiptEmpty>
            {t('LoyaltyPage.NOT_AVAILABLE_RECEIPTS')}
          </Styled.ReceiptEmpty>
        ))}
    </Styled.ReceiptBlock>
  );
};

export default LoyaltyReceiptScreen;
